import type { ReactNode } from 'react';
import { Text } from '../../ui/primitives/Text.tsx';
import { VisuallyHidden } from '../../ui/primitives/VisuallyHidden.tsx';
import styles from './SettingChoice.module.css';

/**
 * A setting with two or three answers, chosen the way a setting should be
 * chosen: by pressing the one you want.
 *
 * NO SAVE. The choice IS the write — the same direct manipulation the name
 * field above it uses. A settings screen that makes you press "Editar", change
 * something, then press "Salvar" has put two steps in front of one decision.
 *
 * NOT `ui/components/ChoiceTile`, though it borrows that tile's surface. The
 * exercise tile's states are `resolved-correct` and `resolved-miss` — they
 * mean "you answered right" and "you answered wrong", which is a judgement a
 * preference can never carry. What IS borrowed is `grad/selected`, the system's
 * neutral acknowledgement of a choice (§ 13 "selection is not judgement"), and
 * the construction every control in `src/ui` uses: a real `<input>` covering
 * the whole target with the drawn surface as its sibling, so the keyboard, the
 * hit area and the announcement stay the platform's and cannot fall out of
 * step with what is actually checked.
 *
 * THE OPTION IS SHOWN IN ITS OWN TERMS. `emphasis="token"` renders the label at
 * `token-lg` — § 5.1's "a short, high-salience string that IS the content, a
 * note name" — which is exactly what the note-naming choice is: the answer to
 * "how will they look" is the names themselves, not a description of them.
 */

export interface SettingChoiceOption<T extends string> {
  value: T;
  label: ReactNode;
  /** One line of consequence, under the label. */
  hint?: string;
  /** `token` for a string that is itself the content; `text` otherwise. */
  emphasis?: 'token' | 'text';
}

interface SettingChoiceProps<T extends string> {
  /** The setting's name, announced as the group's own label. */
  legend: string;
  /**
   * Keeps the legend for the announcement and takes it off the screen, for
   * the one place the heading above it already says the same words — the
   * dialog that asks which plan. The group is still named; it is simply not
   * named twice.
   */
  legendHidden?: boolean;
  /** What the setting decides, in one sentence. */
  description?: ReactNode;
  /** Distinguishes this radio group from every other one on the page. */
  name: string;
  value: T;
  options: readonly SettingChoiceOption<T>[];
  onChange: (value: T) => void;
  /** Mid-request: the group is inert, and says why through the busy label. */
  busy?: boolean;
}

export function SettingChoice<T extends string>({
  legend,
  legendHidden = false,
  description,
  name,
  value,
  options,
  onChange,
  busy = false,
}: SettingChoiceProps<T>) {
  return (
    <fieldset className={styles.group} data-testid={`choice-${name}`}>
      <legend className={styles.legend}>
        {legendHidden ? (
          <VisuallyHidden>{legend}</VisuallyHidden>
        ) : (
          <Text as="h2" variant="subheading">
            {legend}
          </Text>
        )}
      </legend>

      {description ? (
        <div className={styles.description}>
          {typeof description === 'string' ? (
            <Text as="p" variant="body-sm" tone="muted">
              {description}
            </Text>
          ) : (
            description
          )}
        </div>
      ) : null}

      <div className={styles.options}>
        {options.map((option) => (
          <label
            key={option.value}
            className={styles.option}
            data-selected={option.value === value}
            data-testid={`${name}-${option.value}`}
          >
            <input
              className={styles.input}
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === value}
              disabled={busy}
              onChange={() => onChange(option.value)}
            />

            <span className={styles.face}>
              <Text as="span" variant={option.emphasis === 'token' ? 'token-lg' : 'subheading'}>
                {option.label}
              </Text>
              {option.hint ? (
                <Text as="span" variant="body-sm" tone="muted">
                  {option.hint}
                </Text>
              ) : null}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
